import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import ConfirmModal from '../components/ConfirmModal';
import { budgetAPI, ledgerAPI, categoryAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiTarget, FiPlus, FiEdit2, FiTrash2, FiCopy, FiMoreVertical, FiBarChart2, FiZap, FiTrendingUp, FiAlertTriangle, FiInfo, FiChevronLeft, FiChevronRight, FiCalendar, FiDollarSign, FiPieChart } from 'react-icons/fi';
import { format, startOfMonth, endOfMonth, differenceInDays } from 'date-fns';
import CURRENCY, { formatAmount } from '../utils/currency';
import { useTone } from '../contexts/ToneContext';
import './Pages.css';

const Budgets = () => {
  const { t, toneEnabled } = useTone();
  const [budgets, setBudgets] = useState([]);
  const [budgetStatus, setBudgetStatus] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);
  const [selectedBudgetId, setSelectedBudgetId] = useState(null);
  const [activeDetailTab, setActiveDetailTab] = useState('summary'); // 'summary' | 'insights'
  const [report, setReport] = useState(null);
  const [insights, setInsights] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const scrollRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    ledger_id: '',
    category_ids: [],
    all_categories: false,
    budget_type: 'regular',
    period_type: 'monthly',
    planned_event_date: '',
    auto_roll: false,
    start_date: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    end_date: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
  });

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };
    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-select first budget once data loads
  useEffect(() => {
    if (budgets.length > 0 && !selectedBudgetId) {
      setSelectedBudgetId(budgets[0].id);
    }
  }, [budgets, selectedBudgetId]);

  const fetchData = async () => {
    try {
      const [budgetsRes, statusRes, ledgersRes, categoriesRes] = await Promise.allSettled([
        budgetAPI.getAll(),
        budgetAPI.getStatus(),
        ledgerAPI.getAll(),
        categoryAPI.getAll(),
      ]);

      if (budgetsRes.status === 'fulfilled') {
        setBudgets(budgetsRes.value.data.data.budgets || []);
      }
      if (statusRes.status === 'fulfilled') {
        setBudgetStatus(statusRes.value.data.data.budgets || []);
      }
      if (ledgersRes.status === 'fulfilled') {
        setLedgers(ledgersRes.value.data.data.ledgers || []);
      }
      if (categoriesRes.status === 'fulfilled') {
        setCategories(categoriesRes.value.data.data.categories || []);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch insights when detail tab switches
  const fetchInsights = useCallback(async () => {
    if (reportLoading) return;
    setReportLoading(true);
    try {
      const [reportRes, insightsRes] = await Promise.allSettled([
        budgetAPI.getReport(),
        budgetAPI.getInsights(),
      ]);
      if (reportRes.status === 'fulfilled') {
        setReport(reportRes.value.data.data);
      }
      if (insightsRes.status === 'fulfilled') {
        setInsights(insightsRes.value.data.data.insights || []);
      }
    } catch {
      toast.error('Failed to load insights');
    } finally {
      setReportLoading(false);
    }
  }, [reportLoading]);

  useEffect(() => {
    if (activeDetailTab === 'insights' && insights.length === 0 && !reportLoading) {
      fetchInsights();
    }
  }, [activeDetailTab]); // eslint-disable-line

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Budget name is required');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }
    if (formData.category_ids.length === 0) {
      toast.error('Please select at least one category');
      return;
    }

    try {
      const isSpecialEvent = formData.budget_type === 'special_event';
      const submitData = {
        name: formData.name,
        amount: parseFloat(formData.amount),
        ledger_id: formData.ledger_id || null,
        categoryIds: formData.category_ids,
        budget_type: formData.budget_type,
        period: isSpecialEvent ? 'custom' : formData.period_type,
        planned_event_date: isSpecialEvent ? formData.planned_event_date : undefined,
        start_date: isSpecialEvent ? undefined : formData.start_date,
        end_date: isSpecialEvent ? undefined : formData.end_date,
        auto_roll: !isSpecialEvent ? formData.auto_roll : false,
      };

      if (editingId) {
        await budgetAPI.update(editingId, submitData);
        toast.success('Budget updated');
      } else {
        await budgetAPI.create(submitData);
        toast.success('Budget created');
      }

      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleEdit = (budget) => {
    const categoryIds = budget.category_ids || (budget.category_id ? [budget.category_id] : []);
    setFormData({
      name: budget.name,
      amount: parseFloat(budget.amount).toString(),
      ledger_id: budget.ledger_id || '',
      category_ids: categoryIds,
      all_categories: false,
      budget_type: budget.budget_type || 'regular',
      period_type: budget.period_type || budget.period || 'monthly',
      planned_event_date: budget.planned_event_date?.split('T')[0] || '',
      auto_roll: budget.auto_roll || false,
      start_date: budget.start_date?.split('T')[0] || format(startOfMonth(new Date()), 'yyyy-MM-dd'),
      end_date: budget.end_date?.split('T')[0] || format(endOfMonth(new Date()), 'yyyy-MM-dd'),
    });
    setEditingId(budget.id);
    setShowModal(true);
  };

  const handleDuplicate = (budget) => {
    const isSpecialEvent = budget.budget_type === 'special_event';
    const categoryIds = budget.category_ids || (budget.category_id ? [budget.category_id] : []);
    setFormData({
      name: `${budget.name} (Copy)`,
      amount: parseFloat(budget.amount).toString(),
      ledger_id: budget.ledger_id || '',
      category_ids: categoryIds,
      all_categories: false,
      budget_type: budget.budget_type || 'regular',
      period_type: budget.period_type || budget.period || 'monthly',
      planned_event_date: '',
      auto_roll: budget.auto_roll || false,
      start_date: isSpecialEvent ? '' : format(startOfMonth(new Date()), 'yyyy-MM-dd'),
      end_date: isSpecialEvent ? '' : format(endOfMonth(new Date()), 'yyyy-MM-dd'),
    });
    setEditingId(null);
    setShowModal(true);
    if (isSpecialEvent) {
      toast.info('Special event budget copied - please set new event date');
    } else {
      toast.info('Budget copied - modify details and save as new budget');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await budgetAPI.delete(deleteTarget);
      toast.success('Budget deleted');
      if (selectedBudgetId === deleteTarget) {
        setSelectedBudgetId(budgets.find(b => b.id !== deleteTarget)?.id || null);
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to delete budget');
    } finally {
      setDeleteTarget(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      amount: '',
      ledger_id: '',
      category_ids: [],
      all_categories: false,
      budget_type: 'regular',
      period_type: 'monthly',
      planned_event_date: '',
      auto_roll: false,
      start_date: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
      end_date: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
    });
    setEditingId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'budget_type' && value === 'regular') {
        updated.planned_event_date = '';
      }
      return updated;
    });
  };

  const handleCategoryToggle = (categoryId) => {
    setFormData(prev => {
      const ids = prev.category_ids.includes(categoryId)
        ? prev.category_ids.filter(id => id !== categoryId)
        : [...prev.category_ids, categoryId];
      return { ...prev, category_ids: ids };
    });
  };

  const getStatusForBudget = (budgetId) => {
    return budgetStatus.find(s => s.budget_id === budgetId);
  };

  const getProgressPercent = (spent, total) => {
    if (!total || total <= 0) return 0;
    return Math.min((spent / total) * 100, 100);
  };

  const getProgressColor = (percent) => {
    if (percent >= 100) return '#E74C3C';
    if (percent >= 70) return '#F39C12';
    return '#27AE60';
  };

  const getStatusInfo = (percent) => {
    if (percent >= 100) return { text: 'Exceeded', bg: 'var(--budget-status-exceeded-bg, #fde8e8)', color: '#E74C3C' };
    if (percent >= 80) return { text: 'Near Limit', bg: 'var(--budget-status-warning-bg, #fef9e7)', color: '#F39C12' };
    return { text: 'On Track', bg: 'var(--budget-status-safe-bg, #d5f5e3)', color: '#27AE60' };
  };

  const getLedgerName = (id) => {
    const ledger = ledgers.find(l => l.id === id);
    return ledger ? ledger.name : t('budgets.all_ledgers');
  };

  // ENTERPRISE RULE: Filter out "Internal Transfer" system category
  const expenseCategories = categories.filter(c => {
    if (c.is_system && c.name === 'Internal Transfer') return false;
    return c.category_type === 'expense';
  }).sort((a, b) => {
    if (a.is_bookmarked && !b.is_bookmarked) return -1;
    if (!a.is_bookmarked && b.is_bookmarked) return 1;
    if (a.is_bookmarked && b.is_bookmarked) return (a.bookmark_order || 999999) - (b.bookmark_order || 999999);
    return a.name.localeCompare(b.name);
  });

  // Scroll helpers for horizontal budget carousel
  const scrollCarousel = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = 280;
    scrollRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
  };

  // Selected budget derived data
  const selectedBudget = budgets.find(b => b.id === selectedBudgetId);
  const selectedStatus = selectedBudget ? getStatusForBudget(selectedBudget.id) : null;
  const selectedSpent = parseFloat(selectedStatus?.spent_amount || 0);
  const selectedTotal = parseFloat(selectedBudget?.amount || 0);
  const selectedPercent = getProgressPercent(selectedSpent, selectedTotal);
  const selectedRemaining = selectedTotal - selectedSpent;

  // Daily average and projection for selected budget
  const getDailyStats = () => {
    if (!selectedBudget?.start_date) return { dailyAvg: 0, projection: 0, daysLeft: 0 };
    const start = new Date(selectedBudget.start_date);
    const end = selectedBudget.end_date ? new Date(selectedBudget.end_date) : new Date();
    const today = new Date();
    const daysElapsed = Math.max(differenceInDays(today, start), 1);
    const daysTotal = Math.max(differenceInDays(end, start), 1);
    const daysLeft = Math.max(differenceInDays(end, today), 0);
    const dailyAvg = selectedSpent / daysElapsed;
    const projection = dailyAvg * daysTotal;
    return { dailyAvg, projection, daysLeft, daysElapsed, daysTotal };
  };

  const dailyStats = getDailyStats();

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner message={t('common.loading')} />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container">
        {/* Page Header */}
        <div className="page-header">
          <h1><FiTarget style={{ marginRight: 8, verticalAlign: 'middle' }} />{t('budgets.title')}</h1>
          <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
            <FiPlus style={{ marginRight: 4, verticalAlign: 'middle' }} />
            {t('budgets.add')}
          </button>
        </div>

        {budgets.length === 0 ? (
          <EmptyState
            icon={<FiTarget />}
            title={t('budgets.no_budgets')}
            message={t('budgets.create_first_msg')}
            actionLabel={t('budgets.create')}
            onAction={() => { resetForm(); setShowModal(true); }}
          />
        ) : (
          <>
            {/* ===== SECTION 1: SWIPEABLE BUDGET CARDS ===== */}
            <div className="bgt-carousel-wrapper">
              {budgets.length > 2 && (
                <button className="bgt-carousel-arrow bgt-carousel-arrow-left" onClick={() => scrollCarousel('left')} aria-label="Scroll left">
                  <FiChevronLeft size={20} />
                </button>
              )}
              <div className="bgt-carousel" ref={scrollRef}>
                {budgets.map((budget) => {
                  const status = getStatusForBudget(budget.id);
                  const spent = parseFloat(status?.spent_amount || 0);
                  const total = parseFloat(budget.amount);
                  const percent = getProgressPercent(spent, total);
                  const color = getProgressColor(percent);
                  const isSelected = selectedBudgetId === budget.id;
                  const statusInfo = getStatusInfo(percent);

                  return (
                    <div
                      key={budget.id}
                      className={`bgt-card ${isSelected ? 'bgt-card-active' : ''}`}
                      onClick={() => setSelectedBudgetId(budget.id)}
                    >
                      <div className="bgt-card-top">
                        <span className="bgt-card-name">{budget.name}</span>
                        <span className="bgt-card-status" style={{ background: statusInfo.bg, color: statusInfo.color }}>
                          {statusInfo.text}
                        </span>
                      </div>
                      <div className="bgt-card-amount">{formatAmount(total, 0)}</div>
                      <div className="bgt-card-bar">
                        <div className="bgt-card-bar-fill" style={{ width: `${percent}%`, backgroundColor: color }} />
                      </div>
                      <div className="bgt-card-footer">
                        <span>{formatAmount(spent, 0)} {t('budgets.spent')}</span>
                        <span style={{ color, fontWeight: 600 }}>{percent.toFixed(0)}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {budgets.length > 2 && (
                <button className="bgt-carousel-arrow bgt-carousel-arrow-right" onClick={() => scrollCarousel('right')} aria-label="Scroll right">
                  <FiChevronRight size={20} />
                </button>
              )}
            </div>

            {/* ===== SECTION 2: BUDGET DETAIL VIEW ===== */}
            {selectedBudget && (
              <div className="bgt-detail">
                {/* Detail Header with Actions */}
                <div className="bgt-detail-header">
                  <div>
                    <h2 className="bgt-detail-title">{selectedBudget.name}</h2>
                    <div className="bgt-detail-meta">
                      <span className="badge">
                        {selectedBudget.budget_type === 'special_event'
                          ? `Special Event${selectedBudget.planned_event_date ? ` – ${format(new Date(selectedBudget.planned_event_date), 'dd MMM yyyy')}` : ''}`
                          : (selectedBudget.period || selectedBudget.period_type || 'monthly')}
                      </span>
                      <span className="bgt-detail-meta-text">
                        <FiCalendar size={12} style={{ marginRight: 4 }} />
                        {selectedBudget.start_date && format(new Date(selectedBudget.start_date), 'dd MMM')} – {selectedBudget.end_date && format(new Date(selectedBudget.end_date), 'dd MMM yyyy')}
                      </span>
                      <span className="bgt-detail-meta-text">{getLedgerName(selectedBudget.ledger_id)}</span>
                    </div>
                  </div>
                  <div className="bgt-detail-actions">
                    <button className="btn-icon" onClick={() => handleEdit(selectedBudget)} title="Edit">
                      <FiEdit2 size={16} />
                    </button>
                    <button className="btn-icon" onClick={() => handleDuplicate(selectedBudget)} title="Copy">
                      <FiCopy size={16} />
                    </button>
                    <div className="ledger-actions-menu" ref={openMenuId === selectedBudget.id ? menuRef : null}>
                      <button
                        className="btn-icon"
                        onClick={() => setOpenMenuId(openMenuId === selectedBudget.id ? null : selectedBudget.id)}
                        title="More"
                      >
                        <FiMoreVertical size={16} />
                      </button>
                      {openMenuId === selectedBudget.id && (
                        <div className="ledger-dropdown-menu">
                          <button onClick={() => { setDeleteTarget(selectedBudget.id); setOpenMenuId(null); }} style={{ color: '#E74C3C' }}>
                            <FiTrash2 size={14} />
                            <span>{t('action.delete')}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Detail Tabs */}
                <div className="bgt-tabs">
                  {[
                    { key: 'summary', label: 'Summary', icon: <FiPieChart size={14} /> },
                    { key: 'insights', label: 'Insights', icon: <FiZap size={14} /> },
                    { key: 'history', label: 'History', icon: <FiBarChart2 size={14} /> },
                  ].map(tab => (
                    <button
                      key={tab.key}
                      className={`bgt-tab ${activeDetailTab === tab.key ? 'bgt-tab-active' : ''}`}
                      onClick={() => setActiveDetailTab(tab.key)}
                    >
                      {tab.icon} {tab.label}
                    </button>
                  ))}
                </div>

                {/* ===== SUMMARY TAB ===== */}
                {activeDetailTab === 'summary' && (
                  <div className="bgt-summary">
                    {/* Summary Cards Row — hierarchy: Spent (hero) > Remaining (secondary) > Total (subtle) */}
                    <div className="bgt-stats-grid">
                      <div className="bgt-stat-card bgt-stat-card-hero">
                        <div className="bgt-stat-label">Spent</div>
                        <div className="bgt-stat-value" style={{ color: selectedSpent > 0 ? 'var(--color-expense, #E74C3C)' : 'var(--color-text-secondary)' }}>
                          {formatAmount(selectedSpent)}
                        </div>
                      </div>
                      <div className={`bgt-stat-card bgt-stat-card-secondary ${selectedRemaining < 0 ? 'bgt-stat-card-danger' : ''}`}>
                        <div className="bgt-stat-label">Remaining</div>
                        <div className="bgt-stat-value" style={{ color: selectedRemaining >= 0 ? 'var(--color-income, #27AE60)' : 'var(--color-expense, #E74C3C)' }}>
                          {selectedRemaining < 0 && '-'}{formatAmount(Math.abs(selectedRemaining))}
                        </div>
                        {selectedRemaining < 0 && <span className="bgt-stat-over-label">Over budget</span>}
                      </div>
                      <div className="bgt-stat-card bgt-stat-card-subtle">
                        <div className="bgt-stat-label">Budget</div>
                        <div className="bgt-stat-value">{formatAmount(selectedTotal)}</div>
                      </div>
                      <div className="bgt-stat-card bgt-stat-card-subtle">
                        <div className="bgt-stat-label">Utilization</div>
                        <div className="bgt-stat-value" style={{ color: getProgressColor(selectedPercent) }}>
                          {selectedPercent.toFixed(1)}%
                        </div>
                      </div>
                    </div>

                    {/* Large Progress Bar */}
                    <div className="bgt-progress-section">
                      <div className="bgt-progress-large">
                        <div
                          className="bgt-progress-large-fill"
                          style={{
                            width: `${Math.min(selectedPercent, 100)}%`,
                            backgroundColor: getProgressColor(selectedPercent),
                          }}
                        />
                      </div>
                      <div className="bgt-progress-info">
                        <span>{formatAmount(selectedSpent, 0)} of {formatAmount(selectedTotal, 0)}</span>
                        <span style={{ color: getProgressColor(selectedPercent), fontWeight: 600 }}>
                          {selectedPercent.toFixed(1)}%
                        </span>
                      </div>
                    </div>

                    {/* Quick Insights Row */}
                    <div className="bgt-quick-insights">
                      {selectedPercent >= 100 && (
                        <div className="bgt-insight-chip bgt-insight-critical">
                          <FiAlertTriangle size={14} />
                          <span>Over budget by {formatAmount(Math.abs(selectedRemaining))}</span>
                        </div>
                      )}
                      {selectedPercent >= 80 && selectedPercent < 100 && (
                        <div className="bgt-insight-chip bgt-insight-warning">
                          <FiAlertTriangle size={14} />
                          <span>Approaching limit — only {formatAmount(selectedRemaining)} left</span>
                        </div>
                      )}
                      <div className="bgt-insight-chip bgt-insight-info">
                        <FiDollarSign size={14} />
                        <span>Daily avg: {formatAmount(dailyStats.dailyAvg, 0)}</span>
                      </div>
                      <div className="bgt-insight-chip bgt-insight-info">
                        <FiTrendingUp size={14} />
                        <span>Projected: {formatAmount(dailyStats.projection, 0)}</span>
                      </div>
                      {dailyStats.daysLeft > 0 && (
                        <div className="bgt-insight-chip bgt-insight-info">
                          <FiCalendar size={14} />
                          <span>{dailyStats.daysLeft} days left</span>
                        </div>
                      )}
                    </div>

                    {/* Categories Tracked */}
                    <div className="bgt-categories-section">
                      <h4>Categories Tracked</h4>
                      <div className="bgt-categories-list">
                        {(selectedBudget.category_names || selectedStatus?.category_names || 'All expense categories').split(',').map((cat, i) => (
                          <span key={i} className="bgt-category-chip">{cat.trim()}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* ===== INSIGHTS TAB ===== */}
                {activeDetailTab === 'insights' && (
                  <div className="bgt-insights-section">
                    {reportLoading ? (
                      <LoadingSpinner message="Analyzing budgets..." />
                    ) : insights.length === 0 ? (
                      <EmptyState
                        icon={<FiZap />}
                        title="No Insights Yet"
                        message="Insights are generated from your active budgets and spending history."
                      />
                    ) : (
                      <div className="bgt-insights-list">
                        {insights.map((insight, i) => {
                          const iconMap = {
                            over_budget: <FiAlertTriangle size={16} />,
                            near_limit: <FiAlertTriangle size={16} />,
                            under_utilized: <FiInfo size={16} />,
                            category_overspend: <FiTrendingUp size={16} />,
                            spending_trend: <FiTrendingUp size={16} />,
                          };
                          const colorMap = {
                            critical: { bg: '#fde8e8', border: '#E74C3C', color: '#C0392B' },
                            warning: { bg: '#fef9e7', border: '#F39C12', color: '#D68910' },
                            info: { bg: '#ebf5fb', border: '#3498DB', color: '#2471A3' },
                          };
                          const style = colorMap[insight.severity] || colorMap.info;

                          return (
                            <div key={i} className="bgt-insight-card" style={{
                              borderLeft: `4px solid ${style.border}`,
                              background: style.bg,
                            }}>
                              <div className="bgt-insight-icon" style={{ color: style.color }}>
                                {iconMap[insight.type] || <FiInfo size={16} />}
                              </div>
                              <div className="bgt-insight-content">
                                <div className="bgt-insight-type" style={{ color: style.color }}>
                                  {insight.type.replace(/_/g, ' ')}
                                </div>
                                <div className="bgt-insight-message">{insight.message}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* ===== HISTORY TAB ===== */}
                {activeDetailTab === 'history' && (
                  <div className="bgt-history-section">
                    {reportLoading ? (
                      <LoadingSpinner message="Loading history..." />
                    ) : !report || report.periods.length === 0 ? (
                      <EmptyState
                        icon={<FiBarChart2 />}
                        title="No Budget History"
                        message="Enable auto-roll or snapshot a period to start tracking history."
                      />
                    ) : (
                      <>
                        {/* Summary Cards */}
                        <div className="bgt-stats-grid" style={{ marginBottom: 20 }}>
                          <div className="bgt-stat-card">
                            <div className="bgt-stat-label">Total Planned</div>
                            <div className="bgt-stat-value">{formatAmount(report.summary.total_planned)}</div>
                          </div>
                          <div className="bgt-stat-card">
                            <div className="bgt-stat-label">Total Actual</div>
                            <div className="bgt-stat-value" style={{ color: 'var(--color-expense, #E74C3C)' }}>{formatAmount(report.summary.total_actual)}</div>
                          </div>
                          <div className="bgt-stat-card">
                            <div className="bgt-stat-label">Variance</div>
                            <div className="bgt-stat-value" style={{ color: report.summary.total_variance >= 0 ? 'var(--color-income, #27AE60)' : 'var(--color-expense, #E74C3C)' }}>
                              {report.summary.total_variance >= 0 ? '+' : ''}{formatAmount(report.summary.total_variance)}
                            </div>
                          </div>
                          <div className="bgt-stat-card">
                            <div className="bgt-stat-label">Avg Utilization</div>
                            <div className="bgt-stat-value">{report.summary.avg_utilization}%</div>
                          </div>
                        </div>

                        {/* Period Cards (mobile-friendly, no table) */}
                        <div className="bgt-period-list">
                          {report.periods.map((p, i) => {
                            const variance = parseFloat(p.variance);
                            const utilization = parseFloat(p.utilization);
                            return (
                              <div key={i} className="bgt-period-card">
                                <div className="bgt-period-header">
                                  <span className="bgt-period-name">{p.budget_name}</span>
                                  <span className="bgt-period-status" style={{
                                    background: p.status === 'exceeded' ? '#fde8e8' : p.status === 'warning' ? '#fef9e7' : '#d5f5e3',
                                    color: p.status === 'exceeded' ? '#E74C3C' : p.status === 'warning' ? '#F39C12' : '#27AE60',
                                  }}>
                                    {p.status}
                                  </span>
                                </div>
                                <div className="bgt-period-dates">
                                  {format(new Date(p.period_start), 'dd MMM')} – {format(new Date(p.period_end), 'dd MMM yy')}
                                </div>
                                <div className="bgt-period-amounts">
                                  <div>
                                    <span className="bgt-period-label">Planned</span>
                                    <span className="bgt-period-value">{formatAmount(parseFloat(p.planned))}</span>
                                  </div>
                                  <div>
                                    <span className="bgt-period-label">Actual</span>
                                    <span className="bgt-period-value">{formatAmount(parseFloat(p.actual))}</span>
                                  </div>
                                  <div>
                                    <span className="bgt-period-label">Variance</span>
                                    <span className="bgt-period-value" style={{ color: variance >= 0 ? '#27AE60' : '#E74C3C' }}>
                                      {variance >= 0 ? '+' : ''}{formatAmount(variance)}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="bgt-period-label">Util</span>
                                    <span className="bgt-period-value">{utilization.toFixed(1)}%</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ===== CREATE/EDIT MODAL ===== */}
        {showModal && (
          <div className="modal" onClick={() => { setShowModal(false); resetForm(); }}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <h2>{editingId ? t('budgets.edit') : t('budgets.create')}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>{t('budgets.name')}</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g. Monthly Groceries"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('transactions.amount')} ({CURRENCY.symbol})</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    placeholder="10000"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Budget Type</label>
                  <select name="budget_type" value={formData.budget_type} onChange={handleChange}>
                    <option value="regular">Regular (Recurring)</option>
                    <option value="special_event">Special Event (One-time)</option>
                  </select>
                </div>
                {formData.budget_type === 'regular' && (
                  <div className="form-group">
                    <label>{t('budgets.period')}</label>
                    <select name="period_type" value={formData.period_type} onChange={handleChange}>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                )}
                {formData.budget_type === 'special_event' && (
                  <div className="form-group">
                    <label>Planned Event Date *</label>
                    <input
                      type="date"
                      name="planned_event_date"
                      value={formData.planned_event_date}
                      onChange={handleChange}
                      required
                    />
                    <small style={{ color: '#666' }}>Start = today, End = event date + 15 days buffer for vendor payments</small>
                  </div>
                )}
                {formData.budget_type === 'regular' && editingId && (
                  <div style={{ fontSize: '0.85rem', color: '#7F8C8D', marginBottom: '12px', padding: '8px', background: '#f8f9fa', borderRadius: '4px' }}>
                    <strong>Note:</strong> Switching from Special Event to Regular budget will clear event-specific dates.
                  </div>
                )}
                <div className="form-group">
                  <label>{t('transactions.ledger')} ({t('common.optional')})</label>
                  <select name="ledger_id" value={formData.ledger_id} onChange={handleChange}>
                    <option value="">{t('budgets.all_ledgers')}</option>
                    {ledgers.filter(l => !l.is_archived).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('budgets.categories')}</label>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 12px',
                      marginBottom: 8,
                      background: formData.category_ids.length === expenseCategories.length && expenseCategories.length > 0 ? '#e8f5e9' : '#f5f5f5',
                      borderRadius: 6,
                      cursor: 'pointer',
                      border: formData.category_ids.length === expenseCategories.length && expenseCategories.length > 0 ? '1px solid #27AE60' : '1px solid #ddd',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.category_ids.length === expenseCategories.length && expenseCategories.length > 0}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          category_ids: e.target.checked ? expenseCategories.map(c => c.id) : [],
                        }));
                      }}
                    />
                    <strong style={{ color: formData.category_ids.length === expenseCategories.length && expenseCategories.length > 0 ? '#27AE60' : '#2c3e50' }}>
                      {t('budgets.all_categories') || 'Select All Categories'}
                    </strong>
                    <span style={{ color: '#7F8C8D', fontSize: '0.8rem', marginLeft: 'auto' }}>
                      {formData.category_ids.length === expenseCategories.length && expenseCategories.length > 0 ? '(uncheck specific ones below)' : ''}
                    </span>
                  </label>
                  <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 6, padding: 8 }}>
                    {expenseCategories.length === 0 ? (
                      <p style={{ color: '#999', margin: 0 }}>{t('budgets.no_expense_categories')}</p>
                    ) : (
                      expenseCategories.map(c => (
                        <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={formData.category_ids.includes(c.id)}
                            onChange={() => handleCategoryToggle(c.id)}
                          />
                          <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', backgroundColor: c.color || '#888' }} />
                          {c.name}
                        </label>
                      ))
                    )}
                  </div>
                  {formData.category_ids.length > 0 && (
                    <small style={{ color: '#666' }}>{formData.category_ids.length} / {expenseCategories.length} {t('budgets.selected')}</small>
                  )}
                </div>
                {formData.budget_type === 'regular' && (
                  <>
                    <div className="grid grid-2">
                      <div className="form-group">
                        <label>{t('budgets.start_date')}</label>
                        <input type="date" name="start_date" value={formData.start_date} onChange={handleChange} required />
                      </div>
                      <div className="form-group">
                        <label>{t('budgets.end_date')}</label>
                        <input type="date" name="end_date" value={formData.end_date} onChange={handleChange} required />
                      </div>
                    </div>
                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={formData.auto_roll}
                          onChange={(e) => setFormData(prev => ({ ...prev, auto_roll: e.target.checked }))}
                        />
                        <span>Auto-roll to next period</span>
                      </label>
                      <small style={{ color: 'var(--color-text-secondary)', display: 'block', marginTop: 4 }}>
                        When enabled, a new budget is automatically created when this period ends. Categories and limits are copied; spending resets to zero.
                      </small>
                    </div>
                  </>
                )}
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                    {t('action.cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingId ? t('action.update') : t('action.create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {deleteTarget && (
          <ConfirmModal
            title={t('budgets.delete')}
            message={t('budgets.delete_confirm')}
            onConfirm={handleDelete}
            onCancel={() => setDeleteTarget(null)}
          />
        )}
      </div>
    </Layout>
  );
};

export default Budgets;
